import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from './cn'

type Elevation = 'flat' | 'raised' | 'floating'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  elevation?: Elevation
  /** Adds a subtle teal→gold gradient ring around the card */
  accent?: boolean
  /** Removes default padding so callers can lay out edge-to-edge content */
  unpadded?: boolean
}

const elevationClasses: Record<Elevation, string> = {
  flat:     'shadow-none',
  raised:   'shadow-sm',
  floating: 'shadow-md',
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { elevation = 'raised', accent = false, unpadded = false, className, children, ...rest },
  ref,
) {
  if (accent) {
    // Gradient ring via a 1.5px padded wrapper — works without mask gymnastics.
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-2xl p-[1.5px] bg-gradient-to-br from-brand-bright to-accent shadow-glow-gold',
          className,
        )}
        {...rest}
      >
        <div
          className={cn(
            'rounded-[calc(1.25rem-1.5px)] bg-surface h-full w-full',
            elevationClasses[elevation === 'flat' ? 'flat' : 'raised'],
            !unpadded && 'p-6',
          )}
        >
          {children}
        </div>
      </div>
    )
  }
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-2xl bg-surface border border-line',
        elevationClasses[elevation],
        !unpadded && 'p-6',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
})

export function CardHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 flex items-start justify-between gap-3', className)} {...rest} />
}

export function CardTitle({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('font-display text-lg text-ink', className)} {...rest} />
}

export function CardDescription({ className, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-ink-muted', className)} {...rest} />
}
